import { apiClient } from './client';
import type { Company, CompanyMemberRole } from '../types';

export const companiesApi = {
  list: async () => {
    const res = await apiClient.get('/companies');
    return res.data.data.companies as Company[];
  },

  get: async (companyId: string) => {
    const res = await apiClient.get(`/companies/${companyId}`);
    return res.data.data.company as Company;
  },

  create: async (data: { name: string; slug: string; industry?: string }) => {
    const res = await apiClient.post('/companies', data);
    return res.data.data.company as Company;
  },

  applyIndustryDefaults: async (companyId: string, industry: string): Promise<void> => {
    await apiClient.post(`/companies/${companyId}/apply-industry`, { industry });
  },

  update: async (companyId: string, data: Partial<Company>) => {
    const res = await apiClient.put(`/companies/${companyId}`, data);
    return res.data.data.company as Company;
  },

  getMembers: async (companyId: string) => {
    const res = await apiClient.get(`/companies/${companyId}/members`);
    return res.data.data.members;
  },

  addMember: async (companyId: string, email: string, role: CompanyMemberRole) => {
    const res = await apiClient.post(`/companies/${companyId}/members`, { email, role });
    return res.data;
  },

  /** Change a member's role and/or their "update text in emails by default"
   *  flag. Turning the flag on also flips the member's open tickets; the
   *  response says how many. */
  updateMember: async (
    companyId: string,
    userId: string,
    data: { role?: CompanyMemberRole; contentEmailsDefault?: boolean },
  ) => {
    const res = await apiClient.put(`/companies/${companyId}/members/${userId}`, data);
    return res.data as { success: boolean; message: string; data?: { appliedToTickets: number } };
  },

  removeMember: async (companyId: string, userId: string) => {
    await apiClient.delete(`/companies/${companyId}/members/${userId}`);
  },

  getAISettings: async (companyId: string) => {
    const res = await apiClient.get(`/ai/settings`, { headers: { 'X-Company-Id': companyId } });
    return res.data.data as { aiProvider: string | null; aiModel: string | null; hasApiKey: boolean };
  },

  updateAISettings: async (companyId: string, data: { aiProvider?: string; aiApiKey?: string; aiModel?: string }) => {
    const res = await apiClient.put(`/companies/${companyId}/ai-settings`, data);
    return res.data.data as { aiProvider: string | null; aiModel: string | null; hasApiKey: boolean };
  },
};
